import React, { useState, useEffect } from 'react'
import { SparklesIcon, BeakerIcon, ClockIcon, ChartBarIcon, CheckCircleIcon, XCircleIcon, ShieldCheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { aiAPI } from '@/services/api'

type Tab = 'categorization' | 'audit'

interface AuditTransaction {
  transaction_id: number
  description: string
  amount: number
  date: string
  current_category: string
  current_subcategory: string
  suggested_category: string
  suggested_subcategory: string
  confidence: number
  reasoning: string
  ai_provider: string
}

const AIPlayground: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('categorization')

  // Categorization states
  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState('')
  const [merchant, setMerchant] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)
  const [history, setHistory] = useState<any[]>([])

  // Audit states
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditData, setAuditData] = useState<any>(null)
  const [auditError, setAuditError] = useState<string | null>(null)
  const [selectedCorrections, setSelectedCorrections] = useState<Set<number>>(new Set())
  const [applyingCorrections, setApplyingCorrections] = useState(false)

  // Period selection states
  const [periodType, setPeriodType] = useState<'today' | 'last3days' | 'custom'>('last3days')
  const [customStartDate, setCustomStartDate] = useState('')
  const [customEndDate, setCustomEndDate] = useState('')

  const handleCategorize = async () => {
    if (!description || !amount) {
      setError('Preencha descrição e valor')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await aiAPI.categorizeTransaction({
        description,
        amount: parseFloat(amount),
        merchant: merchant || undefined,
        use_ai: true
      })

      setResult(response)

      // Adicionar ao histórico
      setHistory(prev => [{
        description,
        amount: parseFloat(amount),
        merchant,
        result: response,
        timestamp: new Date().toISOString()
      }, ...prev].slice(0, 10)) // Manter últimos 10

    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Erro ao categorizar')
    } finally {
      setLoading(false)
    }
  }

  const handleAuditTransactions = async () => {
    console.log('🔍 [AIPlayground] handleAuditTransactions CHAMADA!')
    console.log('🔍 [AIPlayground] periodType:', periodType)

    setAuditLoading(true)
    setAuditError(null)

    try {
      // Calcular datas baseadas na seleção
      let startDate: string | undefined
      let endDate: string | undefined

      const today = new Date()
      const todayStr = today.toISOString().split('T')[0]

      if (periodType === 'today') {
        startDate = todayStr
        endDate = todayStr
      } else if (periodType === 'last3days') {
        const threeDaysAgo = new Date(today)
        threeDaysAgo.setDate(today.getDate() - 3)
        startDate = threeDaysAgo.toISOString().split('T')[0]
        endDate = todayStr
      } else if (periodType === 'custom') {
        if (!customStartDate || !customEndDate) {
          setAuditError('Selecione as datas de início e fim')
          setAuditLoading(false)
          return
        }

        // Validar período máximo de 7 dias
        const start = new Date(customStartDate)
        const end = new Date(customEndDate)
        const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))

        if (diffDays > 7) {
          setAuditError('Período máximo permitido: 7 dias. Recomendamos 3 dias para melhor performance.')
          setAuditLoading(false)
          return
        }

        startDate = customStartDate
        endDate = customEndDate
      }

      console.log('🚀 [AIPlayground] Chamando API com parâmetros:', {
        start_date: startDate,
        end_date: endDate,
        limit: 50
      })

      const response = await aiAPI.auditTransactions({
        start_date: startDate,
        end_date: endDate,
        limit: 50
      })

      console.log('✅ [AIPlayground] Resposta da API:', response)

      setAuditData(response)
      setSelectedCorrections(new Set())
    } catch (err: any) {
      setAuditError(err.response?.data?.detail || err.message || 'Erro ao auditar transações')
    } finally {
      setAuditLoading(false)
    }
  }

  const handleApplyCorrections = async () => {
    if (selectedCorrections.size === 0) {
      setAuditError('Selecione pelo menos uma correção')
      return
    }

    setApplyingCorrections(true)
    setAuditError(null)

    try {
      const corrections = auditData.suspicious_transactions
        .filter((tx: AuditTransaction) => selectedCorrections.has(tx.transaction_id))
        .map((tx: AuditTransaction) => ({
          transaction_id: tx.transaction_id,
          new_category: tx.suggested_category,
          new_subcategory: tx.suggested_subcategory
        }))

      const response = await aiAPI.applyAuditCorrections(corrections)

      // Atualizar dados após aplicar correções
      await handleAuditTransactions()

      alert(`✅ ${response.applied_count} correções aplicadas com sucesso!`)
    } catch (err: any) {
      setAuditError(err.response?.data?.detail || err.message || 'Erro ao aplicar correções')
    } finally {
      setApplyingCorrections(false)
    }
  }

  const toggleCorrection = (transactionId: number) => {
    const newSelected = new Set(selectedCorrections)
    if (newSelected.has(transactionId)) {
      newSelected.delete(transactionId)
    } else {
      newSelected.add(transactionId)
    }
    setSelectedCorrections(newSelected)
  }

  const toggleAllCorrections = () => {
    if (selectedCorrections.size === auditData?.suspicious_transactions.length) {
      setSelectedCorrections(new Set())
    } else {
      setSelectedCorrections(new Set(auditData?.suspicious_transactions.map((tx: AuditTransaction) => tx.transaction_id)))
    }
  }

  const getProviderBadge = (provider: string) => {
    const badges: Record<string, { color: string, label: string }> = {
      'gemini': { color: 'bg-blue-100 text-blue-800', label: 'Gemini (Google)' },
      'claude': { color: 'bg-purple-100 text-purple-800', label: 'Claude (Anthropic)' },
      'openai': { color: 'bg-green-100 text-green-800', label: 'OpenAI (GPT)' },
      'local_ml': { color: 'bg-yellow-100 text-yellow-800', label: 'Modelo ML Local' },
      'rules': { color: 'bg-gray-100 text-gray-800', label: 'Regras' }
    }

    const badge = badges[provider] || badges['rules']
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
        {badge.label}
      </span>
    )
  }

  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 0.8) {
      return <span className="text-green-600 font-semibold">Alta ({(confidence * 100).toFixed(0)}%)</span>
    } else if (confidence >= 0.6) {
      return <span className="text-yellow-600 font-semibold">Média ({(confidence * 100).toFixed(0)}%)</span>
    } else {
      return <span className="text-red-600 font-semibold">Baixa ({(confidence * 100).toFixed(0)}%)</span>
    }
  }

  const exampleTransactions = [
    { description: 'Aluguel escritório', amount: '-2500.00', merchant: 'Imobiliária ABC' },
    { description: 'Venda produto X', amount: '1500.00', merchant: 'Cliente João' },
    { description: 'Spotify Premium', amount: '-21.90', merchant: 'Spotify' },
    { description: 'Pagamento freelancer', amount: '-800.00', merchant: 'Dev Pedro' },
    { description: 'Recebimento consultoria', amount: '3500.00', merchant: 'Empresa XYZ' }
  ]

  const loadExample = (example: typeof exampleTransactions[0]) => {
    setDescription(example.description)
    setAmount(example.amount)
    setMerchant(example.merchant)
    setResult(null)
    setError(null)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <BeakerIcon className="w-8 h-8 text-purple-600" />
            AI Playground
          </h1>
          <p className="text-gray-600 mt-1">Teste o sistema de categorização inteligente e auditoria com múltiplos agentes de IA</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('categorization')}
            className={`px-4 py-3 border-b-2 font-semibold transition-colors ${
              activeTab === 'categorization'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <SparklesIcon className="w-5 h-5" />
              Categorização
            </div>
          </button>
          <button
            onClick={() => setActiveTab('audit')}
            className={`px-4 py-3 border-b-2 font-semibold transition-colors ${
              activeTab === 'audit'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <ShieldCheckIcon className="w-5 h-5" />
              Auditoria Inteligente
            </div>
          </button>
        </nav>
      </div>

      {/* Tab Content: Categorização */}
      {activeTab === 'categorization' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Form */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <SparklesIcon className="w-5 h-5 text-purple-600" />
                Testar Categorização
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição da Transação *
                  </label>
                  <input
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Ex: Aluguel escritório, Venda produto..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valor * (- para despesa)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="Ex: -2500.00 ou 1500.00"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estabelecimento (opcional)
                    </label>
                    <input
                      type="text"
                      value={merchant}
                      onChange={(e) => setMerchant(e.target.value)}
                      placeholder="Ex: Spotify, Imobiliária"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>

                <button
                  onClick={handleCategorize}
                  disabled={loading || !description || !amount}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-3 rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  {loading ? (
                    <>
                      <ClockIcon className="w-5 h-5 animate-spin" />
                      Categorizando...
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="w-5 h-5" />
                      Categorizar com IA
                    </>
                  )}
                </button>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                    <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Examples */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200 p-6">
              <h3 className="text-sm font-semibold text-purple-900 mb-3">Exemplos Rápidos</h3>
              <div className="space-y-2">
                {exampleTransactions.map((example, idx) => (
                  <button
                    key={idx}
                    onClick={() => loadExample(example)}
                    className="w-full text-left px-3 py-2 rounded-lg bg-white hover:bg-purple-100 transition-colors border border-transparent hover:border-purple-300 text-sm"
                  >
                    <div className="font-medium text-gray-900">{example.description}</div>
                    <div className="text-xs text-gray-600">
                      R$ {example.amount} {example.merchant && `• ${example.merchant}`}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-6">
            {result && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <CheckCircleIcon className="w-5 h-5 text-green-600" />
                    Resultado da Categorização
                  </h2>
                  {getProviderBadge(result.provider)}
                </div>

                <div className="space-y-4">
                  {/* Categorização */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-purple-600 font-medium uppercase mb-1">Categoria</p>
                        <p className="text-lg font-bold text-purple-900">{result.category}</p>
                      </div>
                      <div>
                        <p className="text-xs text-purple-600 font-medium uppercase mb-1">Subcategoria</p>
                        <p className="text-lg font-bold text-purple-900">{result.subcategory}</p>
                      </div>
                    </div>
                  </div>

                  {/* Métricas */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600 mb-1">Confiança</p>
                      <p className="text-sm">{getConfidenceBadge(result.confidence)}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600 mb-1">Método</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {result.method === 'artificial_intelligence' ? 'IA' :
                         result.method === 'machine_learning' ? 'ML' : 'Regras'}
                      </p>
                    </div>
                  </div>

                  {/* Raciocínio */}
                  {result.reasoning && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-xs text-blue-600 font-medium uppercase mb-2">Raciocínio</p>
                      <p className="text-sm text-blue-900">{result.reasoning}</p>
                    </div>
                  )}

                  {/* Sugestões */}
                  {result.suggestions && result.suggestions.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-600 font-medium uppercase mb-2">Sugestões Alternativas</p>
                      <div className="space-y-2">
                        {result.suggestions.map((sug: any, idx: number) => (
                          <div key={idx} className="bg-gray-50 rounded-lg p-3 text-sm">
                            <span className="font-semibold text-gray-900">{sug.category}</span>
                            {' → '}
                            <span className="text-gray-700">{sug.subcategory}</span>
                            <span className="text-gray-500 ml-2">({(sug.confidence * 100).toFixed(0)}%)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Histórico */}
            {history.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <ChartBarIcon className="w-5 h-5 text-gray-600" />
                  Histórico de Testes
                </h2>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {history.map((item, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{item.description}</p>
                          <p className="text-xs text-gray-600 mt-0.5">
                            R$ {item.amount.toFixed(2)} • {item.result.category} → {item.result.subcategory}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          {getProviderBadge(item.result.provider)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!result && history.length === 0 && (
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-12 text-center">
                <BeakerIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Preencha os campos e clique em "Categorizar com IA" para ver os resultados</p>
                <p className="text-sm text-gray-500 mt-2">O sistema tentará usar Gemini, Claude ou OpenAI automaticamente</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab Content: Auditoria */}
      {activeTab === 'audit' && (
        <div className="space-y-6">
          {/* Header e Seletor de Período */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="mb-6">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <ShieldCheckIcon className="w-6 h-6 text-purple-600" />
                Auditoria Inteligente de Categorizações
              </h2>
              <p className="text-sm text-gray-600 mt-1">A IA analisa suas transações e identifica possíveis erros de categorização</p>
            </div>

            {/* Period Selector */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-200 p-4 mb-4">
              <h3 className="text-sm font-semibold text-purple-900 mb-3">Selecionar Período de Análise</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <button
                  onClick={() => setPeriodType('today')}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    periodType === 'today'
                      ? 'border-purple-600 bg-purple-100 text-purple-900'
                      : 'border-purple-200 bg-white text-gray-700 hover:border-purple-300'
                  }`}
                >
                  <div className="text-center">
                    <div className="font-semibold">Hoje</div>
                    <div className="text-xs mt-1 opacity-75">~5-10 transações</div>
                    <div className="text-xs opacity-75">~5 segundos</div>
                  </div>
                </button>

                <button
                  onClick={() => setPeriodType('last3days')}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    periodType === 'last3days'
                      ? 'border-purple-600 bg-purple-100 text-purple-900'
                      : 'border-purple-200 bg-white text-gray-700 hover:border-purple-300'
                  }`}
                >
                  <div className="text-center">
                    <div className="font-semibold">Últimos 3 dias</div>
                    <div className="text-xs mt-1 opacity-75">~30 transações</div>
                    <div className="text-xs opacity-75">~15 segundos</div>
                    <div className="text-xs font-semibold text-green-600 mt-1">RECOMENDADO</div>
                  </div>
                </button>

                <button
                  onClick={() => setPeriodType('custom')}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    periodType === 'custom'
                      ? 'border-purple-600 bg-purple-100 text-purple-900'
                      : 'border-purple-200 bg-white text-gray-700 hover:border-purple-300'
                  }`}
                >
                  <div className="text-center">
                    <div className="font-semibold">Período Customizado</div>
                    <div className="text-xs mt-1 opacity-75">Máx: 7 dias</div>
                    <div className="text-xs opacity-75">Variável</div>
                  </div>
                </button>
              </div>

              {periodType === 'custom' && (
                <div className="grid grid-cols-2 gap-3 p-3 bg-white rounded-lg border border-purple-200">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Data Inicial</label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Data Final</label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={handleAuditTransactions}
              disabled={auditLoading}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-3 rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg transition-all"
            >
              {auditLoading ? (
                <>
                  <ClockIcon className="w-5 h-5 animate-spin" />
                  Auditando...
                </>
              ) : (
                <>
                  <ShieldCheckIcon className="w-5 h-5" />
                  Iniciar Auditoria
                </>
              )}
            </button>

            {auditError && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-800 text-sm">{auditError}</p>
              </div>
            )}
          </div>

          {/* Resultados da Auditoria */}
          {auditData && (
            <>
              {/* Info do Período */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div>
                      <span className="text-sm font-medium text-blue-700">Período analisado:</span>
                      <span className="ml-2 text-sm text-blue-900 font-semibold">
                        {auditData.period_days || 3} dia{(auditData.period_days || 3) > 1 ? 's' : ''}
                      </span>
                    </div>
                    {auditData.start_date && auditData.end_date && (
                      <div className="text-sm text-blue-700">
                        ({new Date(auditData.start_date).toLocaleDateString('pt-BR')} até {new Date(auditData.end_date).toLocaleDateString('pt-BR')})
                      </div>
                    )}
                  </div>
                  {auditData.total_in_period && auditData.total_analyzed && auditData.total_in_period > auditData.total_analyzed && (
                    <div className="text-sm text-blue-700">
                      Analisando {auditData.total_analyzed} de {auditData.total_in_period} transações (priorizadas por valor)
                    </div>
                  )}
                </div>
              </div>

              {/* Estatísticas */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <p className="text-sm text-gray-600">Total Analisado</p>
                  <p className="text-3xl font-bold text-gray-900">{auditData.total_analyzed}</p>
                  {auditData.total_in_period && auditData.total_in_period !== auditData.total_analyzed && (
                    <p className="text-xs text-gray-500 mt-1">
                      de {auditData.total_in_period} no período
                    </p>
                  )}
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <p className="text-sm text-gray-600">Categorizações Suspeitas</p>
                  <p className="text-3xl font-bold text-orange-600">{auditData.suspicious_count}</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <p className="text-sm text-gray-600">Taxa de Acerto</p>
                  <p className="text-3xl font-bold text-green-600">
                    {auditData.total_analyzed > 0
                      ? ((1 - auditData.suspicious_count / auditData.total_analyzed) * 100).toFixed(1)
                      : 0}%
                  </p>
                </div>
              </div>

              {/* Lista de Transações Suspeitas */}
              {auditData.suspicious_transactions.length > 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Categorizações para Revisar ({auditData.suspicious_count})</h3>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={toggleAllCorrections}
                        className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                      >
                        {selectedCorrections.size === auditData.suspicious_transactions.length ? 'Desmarcar Todos' : 'Marcar Todos'}
                      </button>
                      <button
                        onClick={handleApplyCorrections}
                        disabled={selectedCorrections.size === 0 || applyingCorrections}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {applyingCorrections ? (
                          <>
                            <ClockIcon className="w-4 h-4 animate-spin" />
                            Aplicando...
                          </>
                        ) : (
                          <>
                            <CheckCircleIcon className="w-4 h-4" />
                            Aplicar Correções ({selectedCorrections.size})
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
                    {auditData.suspicious_transactions.map((tx: AuditTransaction) => (
                      <div key={tx.transaction_id} className="p-6 hover:bg-gray-50">
                        <div className="flex items-start gap-4">
                          <input
                            type="checkbox"
                            checked={selectedCorrections.has(tx.transaction_id)}
                            onChange={() => toggleCorrection(tx.transaction_id)}
                            className="mt-1 w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                          />

                          <div className="flex-1">
                            {/* Cabeçalho da Transação */}
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h4 className="font-semibold text-gray-900">{tx.description}</h4>
                                <p className="text-sm text-gray-600">
                                  R$ {tx.amount.toFixed(2)} • {new Date(tx.date).toLocaleDateString('pt-BR')}
                                </p>
                              </div>
                              {getProviderBadge(tx.ai_provider)}
                            </div>

                            {/* Categorias: Atual vs Sugerida */}
                            <div className="grid grid-cols-2 gap-4 mb-3">
                              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <p className="text-xs text-red-600 font-medium uppercase mb-1">Categoria Atual (Incorreta)</p>
                                <p className="font-semibold text-red-900">{tx.current_category}</p>
                              </div>
                              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <p className="text-xs text-green-600 font-medium uppercase mb-1">Categoria Sugerida</p>
                                <p className="font-semibold text-green-900">{tx.suggested_category}</p>
                              </div>
                            </div>

                            {/* Raciocínio da IA */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-xs text-blue-600 font-medium uppercase">Raciocínio da IA</p>
                                {getConfidenceBadge(tx.confidence)}
                              </div>
                              <p className="text-sm text-blue-900">{tx.reasoning}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-xl p-12 text-center">
                  <CheckCircleIcon className="w-16 h-16 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-green-900 mb-2">Tudo Certo!</h3>
                  <p className="text-green-800">
                    Nenhuma categorização suspeita encontrada. Suas transações estão bem categorizadas!
                  </p>
                </div>
              )}
            </>
          )}

          {/* Empty State */}
          {!auditData && !auditLoading && (
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-12 text-center">
              <ShieldCheckIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Selecione um período e clique em "Iniciar Auditoria"</p>
              <p className="text-sm text-gray-500 mt-2">
                Recomendamos auditar os últimos 3 dias regularmente para melhor controle de custos e performance
              </p>
            </div>
          )}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200 p-6">
        <h3 className="text-lg font-semibold text-purple-900 mb-3">Como Funciona o Sistema Multi-Agente?</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4">
            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mb-3">
              <span className="text-xl font-bold text-yellow-700">1</span>
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">ML Local</h4>
            <p className="text-xs text-gray-600">Modelo treinado com seus dados (se disponível)</p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mb-3">
              <span className="text-xl font-bold text-blue-700">2</span>
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Gemini</h4>
            <p className="text-xs text-gray-600">Rápido e econômico ($0.50/M tokens)</p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mb-3">
              <span className="text-xl font-bold text-purple-700">3</span>
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Claude</h4>
            <p className="text-xs text-gray-600">Alta qualidade para finanças ($3/M tokens)</p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mb-3">
              <span className="text-xl font-bold text-gray-700">4</span>
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Regras</h4>
            <p className="text-xs text-gray-600">Fallback garantido (sempre funciona)</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AIPlayground
